import React, { useMemo } from 'react';
import { Box, Table, Thead, Tbody, Tr, Th, Td, Text, Flex, VStack, Center } from '@chakra-ui/react';
import { useMyLeagueStore, Team } from '../store/myLeagueStore';
import { useLeagueStandings, StandingRecord } from '../hooks/useLeagueStandings';
import TeamLogo from './TeamLogo';
import GlassCard from './GlassCard';

interface StandingsChartProps {
  fullPage?: boolean;
  view?: 'divisions' | 'wildcard';
}

interface TeamStanding {
  team: Team;
  wins: number;
  losses: number;
  pct: string;
  gb: string;
  l10: string;
  streak: string;
  streakType: 'W' | 'L' | 'none';
}

/**
 * Parse the streak string from the API (e.g. "W5", "L2", "-") into display value and type.
 */
function parseStreak(strk: string): { streak: string; streakType: 'W' | 'L' | 'none' } {
  if (!strk || strk === '-') return { streak: '-', streakType: 'none' };
  if (strk.startsWith('W')) return { streak: strk, streakType: 'W' };
  if (strk.startsWith('L')) return { streak: strk, streakType: 'L' };
  return { streak: strk, streakType: 'none' };
}

const StandingsChart: React.FC<StandingsChartProps> = ({ fullPage = false, view = 'divisions' }) => {
  const myLeague = useMyLeagueStore((state) => state.myLeague);

  // Fetch standings with L10 and streak from the API
  const { data: standingsRecords } = useLeagueStandings(myLeague?.id);

  // Build a lookup from team_id -> StandingRecord for quick access
  const standingsMap = useMemo(() => {
    const map = new Map<number, StandingRecord>();
    if (!standingsRecords) return map;
    for (const rec of standingsRecords) {
      map.set(Number(rec.team_id), rec);
    }
    return map;
  }, [standingsRecords]);

  const standingsData = useMemo(() => {
    if (!myLeague?.conferences || !myLeague?.teamRecords?.length) return null;

    return myLeague.conferences.map(conf => {
      const divisions = conf.divisions.map(div => {
        const divisionTeams: TeamStanding[] = div.teams.map(team => {
          const record = myLeague.teamRecords.find(r => Number(r.team_id) === Number(team.id));
          const apiRecord = standingsMap.get(team.id);
          const w = record?.wins || 0;
          const l = record?.losses || 0;
          const totalGames = w + l;
          const pct = totalGames > 0 ? (w / totalGames).toFixed(3).replace(/^0\./, '.') : '.000';
          const gb = record?.games_behind || '-';

          // Use L10 and streak from the API
          const l10 = apiRecord?.l10 || '-';
          const { streak, streakType } = parseStreak(apiRecord?.strk || '-');

          return {
            team,
            wins: w,
            losses: l,
            pct,
            gb,
            l10,
            streak,
            streakType,
          };
        });

        // Sort by Pct descending, then GB ascending
        divisionTeams.sort((a, b) => {
          if (a.gb === '-' && b.gb !== '-') return -1;
          if (b.gb === '-' && a.gb !== '-') return 1;
          const pctDiff = parseFloat(b.pct) - parseFloat(a.pct);
          if (Math.abs(pctDiff) > 0.001) return pctDiff;
          return parseFloat(a.gb || '0') - parseFloat(b.gb || '0');
        });

        return { ...div, teamsWithRecords: divisionTeams };
      });
      return { ...conf, divisions };
    });
  }, [myLeague, standingsMap]);

  // Build wildcard standings per conference
  const wildcardData = useMemo(() => {
    if (!standingsData) return null;

    return standingsData.map(conf => {
      // Collect non-division-leader teams (exclude the #1 team from each division)
      const divisionLeaderIds = new Set<number>();
      conf.divisions.forEach(div => {
        if (div.teamsWithRecords.length > 0) {
          divisionLeaderIds.add(div.teamsWithRecords[0].team.id);
        }
      });

      const allTeams: TeamStanding[] = [];
      conf.divisions.forEach(div => {
        div.teamsWithRecords.forEach(team => {
          if (!divisionLeaderIds.has(team.team.id)) {
            allTeams.push(team);
          }
        });
      });

      // Sort by pct descending (best record first)
      allTeams.sort((a, b) => {
        const pctDiff = parseFloat(b.pct) - parseFloat(a.pct);
        if (Math.abs(pctDiff) > 0.001) return pctDiff;
        // Tiebreaker: more wins first
        return b.wins - a.wins;
      });

      // Compute wildcard GB relative to the 3rd WC spot (last team in)
      const wcCutoff = Math.min(2, allTeams.length - 1);
      const pivotWins = allTeams.length > 0 ? allTeams[wcCutoff].wins : 0;
      const pivotLosses = allTeams.length > 0 ? allTeams[wcCutoff].losses : 0;

      const teamsWithWcGb = allTeams.map((t, idx) => {
        let wcGb: string;
        if (idx === wcCutoff) {
          wcGb = '-';
        } else {
          const gbVal = ((pivotWins - t.wins) + (t.losses - pivotLosses)) / 2;
          if (gbVal === 0) {
            wcGb = '-';
          } else if (gbVal > 0) {
            // Team is behind the cutoff
            wcGb = gbVal.toFixed(1).replace(/\.0$/, '');
          } else {
            // Team is ahead of the cutoff (negative = games ahead)
            wcGb = `+${Math.abs(gbVal).toFixed(1).replace(/\.0$/, '')}`;
          }
        }
        return { ...t, wcGb };
      });

      const confShort = conf.name === 'American League' ? 'AL'
        : conf.name === 'National League' ? 'NL'
        : conf.name;

      return {
        confName: conf.name,
        confShort,
        teams: teamsWithWcGb,
      };
    });
  }, [standingsData]);

  if (!standingsData) {
    return null;
  }

  const tableSx = {
    borderCollapse: 'collapse' as const,
    'th, td': {
      whiteSpace: 'nowrap' as const,
    },
    'th': {
      fontFamily: "'Exo 2', sans-serif",
      color: 'white',
      fontSize: 'xs',
      fontWeight: 'bold',
      textTransform: 'uppercase' as const,
      borderBottom: '2px solid white',
      py: 2,
      px: 1,
    },
    'td': {
      fontFamily: "'Exo 2', sans-serif",
      color: 'white',
      fontSize: 'sm',
      py: 2,
      px: 1,
      borderBottom: '1px solid rgba(255,255,255,0.1)',
    },
  };

  // Division Standings view
  if (view === 'divisions') {
    return (
      <GlassCard>
        <Box p={{ base: 4, md: 4 }}>
          <VStack spacing={4} align="stretch" >

            {/* Newspaper Header */}
            <Center borderBottom="2px solid white" >
              <Flex w="100%" justify="space-between" align="flex-end">
                <Text as="h3" fontWeight="bold" fontSize={{ base: 'xl', md: 'xl' }} m={0} letterSpacing="0.05em" textTransform="uppercase" >
                  Current Standings
                </Text>
              </Flex>
            </Center>

            {standingsData.map((conf, cIdx) => (
              <Box key={conf.id || cIdx} >

                {/* Massive letters for Conference like "AL" or "NL" */}
                {standingsData.length > 1 && (
                  <Text as="h2" fontWeight="bold" fontSize={{ base: 'xl', md: '2xl' }} m={0} letterSpacing="0.05em" textTransform="uppercase" fontFamily="'Exo 2', sans-serif">
                    {fullPage
                      ? conf.name
                      : conf.name === 'American League'
                      ? 'AL'
                      : conf.name === 'National League'
                      ? 'NL'
                      : conf.name}
                  </Text>
                )}

                <Flex direction="column" gap={6}>
                  {conf.divisions.map((div, dIdx) => (
                    <Box key={div.id || dIdx} w="100%" overflowX="auto">
                      <Table size="sm" variant="unstyled" sx={tableSx}>
                        <Thead>
                          <Tr>
                            <Th width={{ base: '30%', md: '40%' }} className="division-header" fontSize={{ base: 'sm', md: 'md' }} letterSpacing="wide">
                              {div.name}
                            </Th>
                            <Th isNumeric>W</Th>
                            <Th isNumeric>L</Th>
                            <Th isNumeric>Pct</Th>
                            <Th isNumeric>GB</Th>
                            <Th isNumeric>L10</Th>
                            <Th isNumeric>W/L Streak</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {div.teamsWithRecords.map((item, tIdx) => (
                            <Tr key={item.team.id}>
                              <Td>
                                <Flex align="center" gap={{ base: 2, md: 3 }}>
                                  <Text fontWeight="bold" w="18px" textAlign="right">{tIdx + 1}.</Text>
                                  <TeamLogo team={item.team} size="sm" />
                                  <Text fontWeight="bold" textTransform="uppercase" whiteSpace="nowrap">
                                    {item.team.prefix ? `${item.team.prefix} ` : ''}{item.team.name}
                                  </Text>
                                </Flex>
                              </Td>
                              <Td isNumeric>{item.wins}</Td>
                              <Td isNumeric>{item.losses}</Td>
                              <Td isNumeric>{item.pct}</Td>
                              <Td isNumeric>{item.gb}</Td>
                              <Td isNumeric>{item.l10}</Td>
                              <Td isNumeric>
                                <Text
                                  as="span"
                                  color={
                                    item.streakType === 'W' ? 'green.400' :
                                    item.streakType === 'L' ? 'red.400' :
                                    'white'
                                  }
                                  fontWeight="bold"
                                >
                                  {item.streak}
                                </Text>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  ))}
                </Flex>
              </Box>
            ))}
          </VStack>
        </Box>
      </GlassCard>
    );
  }

  // Wildcard Standings view
  if (view === 'wildcard' && wildcardData && wildcardData.length > 0) {
    return (
      <GlassCard>
        <Box p={{ base: 4, md: 4 }}>
          <VStack spacing={6} align="stretch">
            {wildcardData.map((wc, wcIdx) => (
              <Box key={wcIdx} w="100%" overflowX="auto">
                <Table size="sm" variant="unstyled" sx={tableSx}>
                  <Thead>
                    <Tr>
                      <Th width={{ base: '30%', md: '40%' }} className="division-header" fontSize={{ base: 'sm', md: 'md' }} letterSpacing="wide">
                        {fullPage ? `${wc.confName} Wild Card` : `${wc.confShort} Wild Card`}
                      </Th>
                      <Th isNumeric>W</Th>
                      <Th isNumeric>L</Th>
                      <Th isNumeric>Pct</Th>
                      <Th isNumeric>GB</Th>
                      <Th isNumeric>L10</Th>
                      <Th isNumeric>W/L Streak</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {wc.teams.map((item, tIdx) => {
                      const isTopThree = tIdx < 3;
                      const isLastTopThree = tIdx === 2;
                      return (
                        <Tr
                          key={item.team.id}
                          bg={isTopThree ? 'rgba(72, 187, 120, 0.10)' : 'transparent'}
                          sx={{
                            ...(isLastTopThree ? {
                              'td': {
                                borderBottom: '2px solid rgba(72, 187, 120, 0.5) !important',
                              }
                            } : {}),
                          }}
                        >
                          <Td>
                            <Flex align="center" gap={{ base: 2, md: 3 }}>
                              <Text fontWeight="bold" w="18px" textAlign="right">{tIdx + 1}.</Text>
                              <TeamLogo team={item.team} size="sm" />
                              <Text fontWeight="bold" textTransform="uppercase" whiteSpace="nowrap">
                                {item.team.prefix ? `${item.team.prefix} ` : ''}{item.team.name}
                              </Text>
                              {isTopThree && (
                                <Text
                                  as="span"
                                  fontSize="2xs"
                                  fontWeight="bold"
                                  color="green.400"
                                  bg="rgba(72, 187, 120, 0.15)"
                                  px={1.5}
                                  py={0.5}
                                  borderRadius="sm"
                                  ml={1}
                                  textTransform="uppercase"
                                  letterSpacing="wider"
                                >
                                  WC
                                </Text>
                              )}
                            </Flex>
                          </Td>
                          <Td isNumeric>{item.wins}</Td>
                          <Td isNumeric>{item.losses}</Td>
                          <Td isNumeric>{item.pct}</Td>
                          <Td isNumeric>{item.wcGb}</Td>
                          <Td isNumeric>{item.l10}</Td>
                          <Td isNumeric>
                            <Text
                              as="span"
                              color={
                                item.streakType === 'W' ? 'green.400' :
                                item.streakType === 'L' ? 'red.400' :
                                'white'
                              }
                              fontWeight="bold"
                            >
                              {item.streak}
                            </Text>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            ))}
          </VStack>
        </Box>
      </GlassCard>
    );
  }

  return null;
};

export default StandingsChart;
